/**
 * Vehicles repository: CRUD on `vehicles/{plate}`. Reads stay simple;
 * writes that need atomicity (transfer, plate replacement) go through
 * Cloud Functions — not this module — because they update three indices
 * in a single transaction.
 */

import { ref, readOnce } from '../../core/firebase.js';
import { normalize as normalizePlate } from '../../shared/plate.js';

/**
 * @typedef {Object} VehicleRecord
 * @property {string} [ownerId]
 * @property {string} [phone]
 * @property {string} [plate]
 * @property {string} [year]
 * @property {string} [lastOwnership]    ISO date string of most recent baalut.
 * @property {{ vehicle?: Record<string, unknown>; ownership?: any[] }} [gov]
 * @property {Record<string, { lastSeen?: number; ownershipISO?: string }>} [devices]
 * @property {number} [updatedAt]
 */

/**
 * @param {string} plate
 * @returns {Promise<VehicleRecord | null>}
 */
export function get(plate) {
  const p = normalizePlate(plate);
  if (!p) return Promise.resolve(null);
  return readOnce(`vehicles/${p}`);
}

/**
 * @param {string} plate
 * @returns {Promise<string | null>}  Owner UID or null when unclaimed.
 */
export async function getOwnerId(plate) {
  const record = await get(plate);
  return record?.ownerId ?? null;
}

/**
 * Patch vehicle metadata. Use this only for non-ownership fields
 * (gov cache, license dates). Ownership transfers go through Cloud Functions.
 * @param {string} plate
 * @param {Partial<VehicleRecord> & Record<string, unknown>} patch
 * @returns {Promise<void>}
 */
export function update(plate, patch) {
  const p = normalizePlate(plate);
  if (!p) return Promise.reject(new Error('vehicles.update: invalid plate'));
  return ref(`vehicles/${p}`).update({ ...patch, updatedAt: Date.now() });
}

/**
 * Record this device as authorized for this plate. The watcher elsewhere
 * listens on `vehicles/{plate}/devices/{deviceId}` deletions to force-logout
 * revoked devices.
 * @param {string} plate
 * @param {string} deviceId
 * @returns {Promise<void>}
 */
export function authorizeDevice(plate, deviceId) {
  const p = normalizePlate(plate);
  if (!p || !deviceId) return Promise.resolve();
  return ref(`vehicles/${p}/devices/${deviceId}`).update({
    lastSeen: Date.now(),
  });
}

/**
 * Remove a device authorization (e.g. when the user logs out from this device).
 * @param {string} plate
 * @param {string} deviceId
 */
export function revokeDevice(plate, deviceId) {
  const p = normalizePlate(plate);
  if (!p || !deviceId) return Promise.resolve();
  return ref(`vehicles/${p}/devices/${deviceId}`).remove();
}

/**
 * Subscribe to live changes on the vehicle record. Returns an unsubscribe.
 * @param {string} plate
 * @param {(record: VehicleRecord | null) => void} handler
 * @returns {() => void}
 */
export function subscribe(plate, handler) {
  const p = normalizePlate(plate);
  if (!p) return () => {};
  const r = ref(`vehicles/${p}`);
  const cb = (/** @type {any} */ snap) => handler(snap.val() ?? null);
  r.on('value', cb);
  return () => { try { r.off('value', cb); } catch { /* ignore */ } };
}
